/**
 * SurveyWorkspace — Fase 2 (viewer + management)
 *
 * @remarks
 * Dos modos de renderizado:
 * - Normal: WorkspaceShell + split layout (gestión de sesiones).
 * - Viewer: fullscreen con sidebar + mapa full canvas (sin WorkspaceShell).
 *
 * Patrón idéntico a InventoryWorkspace en modo viewer:
 * layout escapa del WorkspaceShell usando position:fixed para que el mapa
 * ocupe toda la viewport.
 */

import { useState } from 'react';
import { CaptureViewer } from '../modules/survey/components/CaptureViewer';
import { SessionDetailPanel } from '../modules/survey/components/SessionDetailPanel';
import { SessionForm } from '../modules/survey/components/SessionForm';
import { SessionList } from '../modules/survey/components/SessionList';
import { useSurveySessions } from '../modules/survey/hooks/useSurveySessions';
import type { SurveySession } from '../modules/survey/types/survey.types';
import { useProject } from '../projects';
import { WorkspaceShell } from './WorkspaceShell';
import './survey-workspace.css';

// ── Local sub-components ───────────────────────────────────────────────────────

interface ViewerLayoutProps {
  viewerSession: SurveySession;
  sessions: SurveySession[];
  isLoading: boolean;
  error: string | null;
  projectId: string | null;
  onSelect: (s: SurveySession) => void;
  onCloseViewer: () => void;
  onNewSession: () => void;
}

function ViewerLayout({
  viewerSession,
  sessions,
  isLoading,
  error,
  projectId,
  onSelect,
  onCloseViewer,
  onNewSession,
}: ViewerLayoutProps): JSX.Element {
  return (
    <div className="sv-fullscreen">
      <div className="sv-sidebar">
        <div className="sv-sidebar-header">
          <div className="sv-sidebar-toprow">
            <h2 className="sv-sidebar-title">Sesiones</h2>
            <button
              type="button"
              className="sw-btn-new"
              disabled={!projectId}
              onClick={onNewSession}
            >
              + Nueva
            </button>
          </div>
          <button type="button" className="sv-sidebar-back" onClick={onCloseViewer}>
            ← Volver a detalle
          </button>
        </div>
        <div className="sv-sidebar-list">
          <SessionList
            sessions={sessions}
            isLoading={isLoading}
            error={error}
            selectedId={viewerSession.id}
            onSelect={onSelect}
          />
        </div>
      </div>
      <CaptureViewer session={viewerSession} onClose={onCloseViewer} />
    </div>
  );
}

interface RightPanelProps {
  isCreating: boolean;
  projectId: string | null;
  selectedSession: SurveySession | null;
  onCreated: (s: SurveySession) => void;
  onCancelCreate: () => void;
  onCloseDetail: () => void;
  onOpenViewer: (s: SurveySession) => void;
}

function RightPanel({
  isCreating,
  projectId,
  selectedSession,
  onCreated,
  onCancelCreate,
  onCloseDetail,
  onOpenViewer,
}: RightPanelProps): JSX.Element {
  if (isCreating && projectId) {
    return <SessionForm projectId={projectId} onCreated={onCreated} onCancel={onCancelCreate} />;
  }
  if (selectedSession) {
    return (
      <SessionDetailPanel
        key={selectedSession.id}
        initialSession={selectedSession}
        onClose={onCloseDetail}
        onOpenViewer={onOpenViewer}
      />
    );
  }
  return (
    <div className="sw-empty-detail">
      <p className="sw-empty-detail-text">
        {projectId
          ? 'Seleccioná una sesión o creá una nueva.'
          : 'Seleccioná un proyecto para ver las sesiones.'}
      </p>
    </div>
  );
}

// ── Main workspace ─────────────────────────────────────────────────────────────

export function SurveyWorkspace(): JSX.Element {
  const { projectId } = useProject();
  const { sessions, isLoading, error, reload } = useSurveySessions({
    projectId: projectId ?? '',
    autoLoad: !!projectId,
  });
  const [selectedSession, setSelectedSession] = useState<SurveySession | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [viewerSession, setViewerSession] = useState<SurveySession | null>(null);

  function handleCreated(session: SurveySession): void {
    void reload();
    setIsCreating(false);
    setSelectedSession(session);
  }

  function handleSelect(session: SurveySession): void {
    setIsCreating(false);
    setSelectedSession(session);
    setViewerSession(null);
  }

  function handleOpenViewer(session: SurveySession): void {
    setViewerSession(session);
    setIsCreating(false);
  }

  if (viewerSession) {
    return (
      <ViewerLayout
        viewerSession={viewerSession}
        sessions={sessions}
        isLoading={isLoading}
        error={error}
        projectId={projectId}
        onSelect={handleSelect}
        onCloseViewer={() => setViewerSession(null)}
        onNewSession={() => {
          setViewerSession(null);
          setSelectedSession(null);
          setIsCreating(true);
        }}
      />
    );
  }

  return (
    <WorkspaceShell title="Relevamiento Territorial" status="active">
      <div className="sw-workspace">
        <div className="sw-left-panel">
          <div className="sw-left-header">
            <h2 className="sw-left-title">Sesiones</h2>
            <button
              type="button"
              className="sw-btn-new"
              disabled={!projectId}
              onClick={() => {
                setSelectedSession(null);
                setIsCreating(true);
              }}
            >
              + Nueva
            </button>
          </div>
          <div className="sw-left-summary">
            {sessions.length > 0 && (
              <span>
                {sessions.length} sesión{sessions.length !== 1 ? 'es' : ''}
              </span>
            )}
          </div>
          <div className="sw-list">
            <SessionList
              sessions={sessions}
              isLoading={isLoading}
              error={error}
              selectedId={selectedSession?.id ?? null}
              onSelect={handleSelect}
            />
          </div>
        </div>
        <div className="sw-right-panel">
          <RightPanel
            isCreating={isCreating}
            projectId={projectId}
            selectedSession={selectedSession}
            onCreated={handleCreated}
            onCancelCreate={() => setIsCreating(false)}
            onCloseDetail={() => setSelectedSession(null)}
            onOpenViewer={handleOpenViewer}
          />
        </div>
      </div>
    </WorkspaceShell>
  );
}
